import { all, request, uploadFile, withQuery } from './api';
import { canRead, type Capability, type Role, type SharedData, type StaffUser } from './accounts';
import type { PayoutBasis } from './earnings';
import type {
  DocumentKind,
  DocumentReviewState,
  InterviewOutcome,
  InterviewSlot,
  PartnerApplication,
} from './applications';
import type { Customer } from './customers';
import type {
  Booking,
  BookingStatus,
  CancellationQuote,
  Coupon,
  KitAdjustInput,
  KitFulfilInput,
  KitIssueInput,
  KitMovement,
  KitMovementKind,
  KitRequestStatus,
  KitRestockRequest,
  KitsSummary,
  KitStockRow,
  KitStockSort,
  KitStockStatus,
  KitKind,
  KitReturnInput,
  KitType,
  KitTypeSaveInput,
  CounterLookup,
  DeliveryRecord,
  JobEarnings,
  Notification,
  OrderPayments,
  PartnerBalance,
  PaymentConfig,
  PayoutGatewayConfig,
  PayoutRecord,
  Professional,
  ProfessionalKits,
  RefundRecord,
  Service,
  ServiceCategory,
  ServiceSaveInput,
  WarehouseItem,
  WarehouseMovement,
  WarehouseMovementKind,
} from './types';

/** One page of a server-paged list. */
export interface Paged<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type ArchivedFilter = 'exclude' | 'only' | 'include';

export interface KitStockQuery {
  professionalId: string;
  kitTypeId: string;
  /** '' for everything; `gear` is uniforms and equipment together. */
  kind: KitKind | 'gear' | '';
  /** '' for every status; `attention` is low and out together. */
  status: KitStockStatus | 'attention' | '';
  q: string;
  sort: KitStockSort;
  page: number;
  pageSize: number;
}

export interface KitMovementQuery {
  professionalId: string;
  kitTypeId: string;
  kind: KitMovementKind | '';
  jobId: string;
  page: number;
  pageSize: number;
}

export interface KitRequestQuery {
  /** Empty for every status. */
  statuses: KitRequestStatus[];
  professionalId: string;
  sort: 'newest' | 'oldest';
  page: number;
  pageSize: number;
}

export interface WarehouseMovementQuery {
  kitTypeId: string;
  professionalId: string;
  kind: WarehouseMovementKind | '';
  page: number;
  pageSize: number;
}

export interface ReceiveStockInput {
  quantity: number;
  /** The delivery's batch number or invoice. */
  reference: string;
  note: string;
}

/** Where a payout goes, decrypted for finance. Never stored in the console. */
export interface PayoutDestination {
  accountHolderName: string;
  accountNumber: string;
  ifsc: string;
  bankName: string;
}

/** Finance recording what became of a manual transfer. */
export interface PayoutSettleInput {
  status: 'processing' | 'paid' | 'failed';
  /** The bank's UTR; required for `paid`. */
  reference?: string;
  /** Required for `failed`; the partner sees it. */
  failureReason?: string;
}

/** A manual ledger line. Only an adjustment may be negative. */
export interface LedgerEntryInput {
  kind: 'adjustment' | 'incentive' | 'tip';
  amount: number;
  label: string;
  note: string;
}

async function paged<T>(path: string): Promise<Paged<T>> {
  const { data, meta } = await request<T[]>(path);
  const pageSize = meta?.pageSize || data.length || 1;
  const total = meta?.total ?? data.length;
  return {
    rows: data,
    total,
    page: meta?.page ?? 1,
    pageSize,
    totalPages: meta?.totalPages ?? Math.max(1, Math.ceil(total / pageSize)),
  };
}

const segment = (value: string) => encodeURIComponent(value);

export interface AuditEntryDto {
  id: string;
  at: string;
  actor: string;
  action: string;
  target: string;
}

export interface SettingsDto {
  businessName: string;
  supportEmail: string;
  supportPhone: string;
  taxRatePercent: number;
  cancellationWindowHours: number;
  sameDayLeadHours: number;
  openingTime: string;
  closingTime: string;
  cities: string[];
  acceptingBookings: boolean;
  memberDiscountPercent: number;
  /** KAYRA Care's cut of each booking, in percent. Partners earn the rest. */
  commissionRatePercent: number;
  /** What the partner's share is a percentage of. */
  payoutBasis: PayoutBasis;
  /** The smallest withdrawal a partner may request, in rupees. */
  minPayoutRupees: number;
}

export interface ModeratedReviewDto {
  id: string;
  bookingId: string;
  customerName: string;
  customerId?: string;
  professionalId?: string;
  serviceId?: string;
  rating: number;
  comment: string;
  createdAt: string;
  state: 'published' | 'pending' | 'hidden';
  moderationNote?: string;
  moderatedBy?: string;
  moderatedAt?: string;
}

/** A console login as the Employees page saves it. */
export interface StaffSaveInput {
  id?: string;
  name: string;
  email: string;
  role: Role;
  /** An employee's pages. Ignored for an administrator, who has every page. */
  capabilities: Capability[];
  phone: string;
  jobTitle: string;
  employeeCode: string;
  notes: string;
  city: string | null;
  /** Only when creating the login. */
  temporaryPassword?: string;
}

/** A home carousel slide as the console edits it. */
export interface AdminBanner {
  id: string;
  onWeb: boolean;
  onApp: boolean;
  audience: BannerAudience;
  eyebrow: string;
  title: string;
  subtitle: string;
  cta: string;
  /** A destination: services, professionals, offers, category:<id>, service:<id> or https://… */
  link: string;
  webHref: string;
  appHref: string;
  /** False when the category or package it names is missing or unpublished. */
  linkOk: boolean;
  image: string;
  imageAlt: string;
  subjectSide: 'left' | 'right';
  focusX: number;
  focusY: number;
  heroFocusX: number;
  heroFocusY: number;
  heroZoom: number;
  position: number;
  active: boolean;
  updatedBy: string;
  updatedAt: string;
}

export type BannerAudience = 'all' | 'female' | 'male';

export type BannerSaveInput = Omit<
  AdminBanner,
  'id' | 'webHref' | 'appHref' | 'linkOk' | 'position' | 'updatedBy' | 'updatedAt'
> & { id?: string };

/** The website hero's words as the console edits them. */
export interface AdminHero {
  overline: string;
  headline: string;
  headlineAccent: string;
  intro: string;
  primaryLabel: string;
  primaryLink: string;
  primaryHref: string;
  secondaryLabel: string;
  secondaryLink: string;
  secondaryHref: string;
  updatedBy: string;
  updatedAt: string;
}

export type HeroSaveInput = Pick<
  AdminHero,
  | 'overline'
  | 'headline'
  | 'headlineAccent'
  | 'intro'
  | 'primaryLabel'
  | 'primaryLink'
  | 'secondaryLabel'
  | 'secondaryLink'
>;

/** An image the gateway stored. `url` is what an image field keeps. */
export interface MediaUpload {
  url: string;
  mimeType: string;
  size: number;
  width: number;
  height: number;
}

interface CustomerWire extends Omit<Customer, 'name'> {
  firstName: string;
  lastName: string;
}

interface ApplicationWire extends PartnerApplication {
  interview?: InterviewSlot & {
    attended?: boolean;
    skillScore?: number;
    conductScore?: number;
    recordedBy?: string;
    recordedAt?: string;
  };
}

type AdminCategory = ServiceCategory & {
  position?: number;
  active?: boolean;
  metaTitle?: string;
  metaDescription?: string;
};

function customer(wire: CustomerWire): Customer {
  return { ...wire, name: `${wire.firstName} ${wire.lastName}`.trim() || 'Guest' };
}

function application(wire: ApplicationWire): PartnerApplication {
  const interview = wire.interview;
  return {
    ...wire,
    timeline: wire.timeline || [],
    documents: wire.documents || [],
    ...(interview?.recordedAt && interview.attended !== undefined
      ? {
          outcome: {
            attended: interview.attended,
            skillScore: interview.skillScore || 0,
            conductScore: interview.conductScore || 0,
            notes: interview.notes || '',
            recordedBy: interview.recordedBy || '',
            recordedAt: interview.recordedAt,
          },
        }
      : {}),
  };
}

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

export const adminApi = {
  /**
   * Everything the console holds, as far as this account may read it. A list it may
   * not read is left empty rather than asked for: the API would refuse it, and one
   * refusal would fail the whole load.
   */
  async bootstrap(user: StaffUser | undefined) {
    const when = <T>(data: SharedData, load: () => Promise<T[]>): Promise<T[]> =>
      canRead(user, data) ? load() : Promise.resolve([]);

    const [
      bookings,
      services,
      categories,
      professionals,
      customers,
      coupons,
      reviews,
      notifications,
      staff,
      settings,
      audit,
      applications,
      kitTypes,
    ] = await Promise.all([
      when('bookings', () => all<Booking>('/admin/bookings')),
      all<Service>('/admin/services'),
      request<AdminCategory[]>('/admin/categories').then((value) => value.data),
      when('professionals', () => all<Professional>('/admin/professionals')),
      when('customers', () => all<CustomerWire>('/admin/customers').then((rows) => rows.map(customer))),
      when('coupons', () => request<Coupon[]>('/admin/coupons').then((value) => value.data)),
      when('reviews', () => all<ModeratedReviewDto>('/admin/reviews')),
      when('notifications', () => all<Notification>('/admin/notifications/')),
      when('staff', () => request<StaffUser[]>('/admin/staff').then((value) => value.data)),
      request<SettingsDto>('/admin/settings').then((value) => value.data),
      when('audit', () => all<AuditEntryDto>('/admin/audit')),
      when('applications', () =>
        all<ApplicationWire>('/admin/applications?filter=all').then((rows) => rows.map(application)),
      ),
      request<KitType[]>('/admin/kit-types').then((value) => value.data),
    ]);
    return {
      bookings,
      services,
      categories,
      professionals,
      customers,
      coupons,
      reviews,
      notifications,
      staff,
      settings,
      audit,
      applications,
      kitTypes,
    };
  },

  async audit() {
    return all<AuditEntryDto>('/admin/audit');
  },

  async application(id: string) {
    return application((await request<ApplicationWire>(`/admin/applications/${id}`)).data);
  },
  async professional(id: string) {
    return (await request<Professional>(`/admin/professionals/${id}`)).data;
  },
  async reviewDocument(id: string, kind: DocumentKind, state: DocumentReviewState, note?: string) {
    return application(
      (
        await request<ApplicationWire>(`/admin/applications/${id}/documents/${kind}`, {
          method: 'PATCH',
          body: { state, note: note || '' },
        })
      ).data,
    );
  },
  async startReview(id: string) {
    return application(
      (await request<ApplicationWire>(`/admin/applications/${id}/start-review`, { method: 'POST' })).data,
    );
  },
  async scheduleInterview(id: string, slot: InterviewSlot) {
    return application(
      (await request<ApplicationWire>(`/admin/applications/${id}/interview`, { method: 'POST', body: slot })).data,
    );
  },
  async recordOutcome(id: string, outcome: Omit<InterviewOutcome, 'recordedAt'>) {
    return application(
      (
        await request<ApplicationWire>(`/admin/applications/${id}/interview/outcome`, {
          method: 'POST',
          body: {
            attended: outcome.attended,
            skillScore: outcome.skillScore,
            conductScore: outcome.conductScore,
            notes: outcome.notes,
          },
        })
      ).data,
    );
  },
  async approve(id: string) {
    return application(
      (await request<ApplicationWire>(`/admin/applications/${id}/approve`, { method: 'POST' })).data,
    );
  },
  async reject(id: string, reason: string) {
    return application(
      (await request<ApplicationWire>(`/admin/applications/${id}/reject`, { method: 'POST', body: { reason } })).data,
    );
  },
  async applicationNotes(id: string, notes: string) {
    return application(
      (await request<ApplicationWire>(`/admin/applications/${id}/notes`, { method: 'PATCH', body: { notes } })).data,
    );
  },

  /**
   * What a cancellation now would mean for the customer. `onCustomersBehalf` prices it
   * as theirs (late charge), otherwise as ours (full refund, credit if late).
   */
  async cancellationQuote(id: string, onCustomersBehalf: boolean) {
    return (
      await request<CancellationQuote>(
        withQuery(`/admin/bookings/${id}/cancellation`, { onCustomersBehalf: String(onCustomersBehalf) }),
      )
    ).data;
  },
  /** Cancels with a reason the customer is shown. */
  async cancelBooking(id: string, reason: string, onCustomersBehalf: boolean) {
    return (
      await request<Booking>(`/admin/bookings/${id}/cancel`, {
        method: 'POST',
        body: { reason, onCustomersBehalf },
      })
    ).data;
  },
  async bookingStatus(id: string, status: BookingStatus) {
    return (
      await request<Booking>(`/admin/bookings/${id}/status`, {
        method: 'POST',
        body: { status, note: 'Updated by operations.' },
      })
    ).data;
  },
  async reassignBooking(booking: Booking, professionalId: string) {
    if (!booking.jobId) throw new Error('This booking has no dispatch job to reassign.');
    await request(`/admin/jobs/${booking.jobId}/reassign`, {
      method: 'POST',
      body: { professionalId },
    });

    // Booking owns the customer-facing projection and receives the partner update
    // through the outbox. Wait briefly for that projection instead of reporting a
    // stale therapist as a successful reassignment.
    for (const delay of [0, 100, 250, 500, 1_000]) {
      if (delay) await wait(delay);
      const updated = (await request<Booking>(`/admin/bookings/${booking.id}`)).data;
      if (updated.professionalId === professionalId) return updated;
    }
    throw new Error('Reassignment was accepted, but the booking view has not caught up. Refresh shortly.');
  },
  async rescheduleBooking(id: string, date: string, time: string) {
    return (
      await request<Booking>(`/admin/bookings/${id}/reschedule`, {
        method: 'POST',
        body: { date, time },
      })
    ).data;
  },

  /* Packages (the catalogue's `services`) ---------------------------------- */

  async services(archived: ArchivedFilter = 'exclude') {
    return all<Service>(withQuery('/admin/services', { archived }));
  },
  async service(key: string) {
    return (await request<Service>(`/admin/services/${segment(key)}`)).data;
  },
  /**
   * Creates (no id) or replaces a package with its types and add-ons. The body is
   * built field by field because the server refuses unknown keys.
   */
  async saveService(id: string | undefined, input: ServiceSaveInput) {
    const body: ServiceSaveInput = {
      slug: input.slug,
      categoryId: input.categoryId,
      targetGender: input.targetGender,
      title: input.title,
      description: input.description,
      image: input.image,
      gallery: input.gallery,
      includes: input.includes,
      faqs: input.faqs.map((faq) => ({ q: faq.q, a: faq.a })),
      trending: input.trending,
      popular: input.popular,
      active: input.active,
      cities: input.cities,
      specialties: input.specialties,
      position: input.position,
      metaTitle: input.metaTitle,
      metaDescription: input.metaDescription,
      links: input.links.map((link) => ({ title: link.title, url: link.url })),
      kitRequirements: input.kitRequirements.map(({ kitTypeId, quantity }) => ({ kitTypeId, quantity })),
      tiers: input.tiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        description: tier.description,
        image: tier.image,
        price: tier.price,
        duration: tier.duration,
        includes: tier.includes,
        kitRequirements: tier.kitRequirements.map(({ kitTypeId, quantity }) => ({ kitTypeId, quantity })),
        active: tier.active,
      })),
      addons: input.addons.map((addon) => ({
        id: addon.id,
        name: addon.name,
        description: addon.description,
        image: addon.image,
        price: addon.price,
        duration: addon.duration,
        maxQuantity: addon.maxQuantity,
        kitRequirements: addon.kitRequirements.map(({ kitTypeId, quantity }) => ({ kitTypeId, quantity })),
        active: addon.active,
      })),
    };
    const path = id ? `/admin/services/${segment(id)}` : '/admin/services';
    return (await request<Service>(path, { method: id ? 'PUT' : 'POST', body })).data;
  },
  /** The console's "Delete": archives the package. Bookings and reviews keep it. */
  async archiveService(id: string) {
    await request<{ archived: boolean }>(`/admin/services/${segment(id)}`, { method: 'DELETE' });
  },
  /** Back from the archive, unpublished. */
  async restoreService(id: string) {
    return (await request<Service>(`/admin/services/${segment(id)}/restore`, { method: 'POST' })).data;
  },
  async publishService(id: string, active: boolean) {
    return (
      await request<Service>(`/admin/services/${segment(id)}/publish`, {
        method: 'POST',
        body: { active },
      })
    ).data;
  },

  /* Kit catalogue ------------------------------------------------------------ */

  async kitTypes() {
    return (await request<KitType[]>('/admin/kit-types')).data;
  },
  /** PUT replaces every field, so the whole kit type is always sent. */
  async saveKitType(id: string | undefined, input: KitTypeSaveInput) {
    const body: KitTypeSaveInput = {
      sku: input.sku,
      name: input.name,
      kind: input.kind,
      categoryId: input.categoryId,
      description: input.description,
      unit: input.unit,
      image: input.image,
      lowStockThreshold: input.lowStockThreshold,
      active: input.active,
      position: input.position,
    };
    const path = id ? `/admin/kit-types/${segment(id)}` : '/admin/kit-types';
    return (await request<KitType>(path, { method: id ? 'PUT' : 'POST', body })).data;
  },
  /** Deactivates. Refused with 409 while live packages still use the kit. */
  async deactivateKitType(id: string) {
    await request<{ deactivated: boolean }>(`/admin/kit-types/${segment(id)}`, { method: 'DELETE' });
  },

  /* Kit stock ---------------------------------------------------------------- */

  async kitSummary() {
    return (await request<KitsSummary>('/admin/kits/summary')).data;
  },
  async kitStock(query: KitStockQuery) {
    return paged<KitStockRow>(
      withQuery('/admin/kits/stock', {
        professionalId: query.professionalId,
        kitTypeId: query.kitTypeId,
        kind: query.kind,
        status: query.status,
        q: query.q.trim(),
        sort: query.sort,
        page: query.page,
        pageSize: query.pageSize,
      }),
    );
  },
  /** Every low or out cell, for per-professional badges. */
  async kitAttention() {
    return all<KitStockRow>(withQuery('/admin/kits/stock', { status: 'attention', sort: 'professional' }));
  },
  async kitMovements(query: KitMovementQuery) {
    return paged<KitMovement>(
      withQuery('/admin/kits/movements', {
        professionalId: query.professionalId,
        kitTypeId: query.kitTypeId,
        kind: query.kind,
        jobId: query.jobId,
        page: query.page,
        pageSize: query.pageSize,
      }),
    );
  },
  async kitRequests(query: KitRequestQuery) {
    return paged<KitRestockRequest>(
      withQuery('/admin/kits/requests', {
        status: query.statuses.join(','),
        professionalId: query.professionalId,
        sort: query.sort,
        page: query.page,
        pageSize: query.pageSize,
      }),
    );
  },
  /** Items omitted issues exactly what was requested; a list replaces it. */
  async fulfilKitRequest(id: string, input: KitFulfilInput) {
    return (
      await request<KitRestockRequest>(`/admin/kits/requests/${segment(id)}/fulfil`, {
        method: 'POST',
        body: {
          ...(input.items
            ? { items: input.items.map(({ kitTypeId, quantity }) => ({ kitTypeId, quantity })) }
            : {}),
          note: input.note,
        },
      })
    ).data;
  },
  async rejectKitRequest(id: string, reason: string) {
    return (
      await request<KitRestockRequest>(`/admin/kits/requests/${segment(id)}/reject`, {
        method: 'POST',
        body: { reason },
      })
    ).data;
  },
  async professionalKits(professionalId: string) {
    return (await request<ProfessionalKits>(`/admin/professionals/${segment(professionalId)}/kits`)).data;
  },
  async issueKits(professionalId: string, input: KitIssueInput) {
    return (
      await request<ProfessionalKits>(`/admin/professionals/${segment(professionalId)}/kits/issue`, {
        method: 'POST',
        body: {
          items: input.items.map(({ kitTypeId, quantity }) => ({ kitTypeId, quantity })),
          note: input.note,
          ...(input.restockRequestId ? { restockRequestId: input.restockRequestId } : {}),
        },
      })
    ).data;
  },
  async adjustKit(professionalId: string, input: KitAdjustInput) {
    return (
      await request<ProfessionalKits>(`/admin/professionals/${segment(professionalId)}/kits/adjust`, {
        method: 'POST',
        body: { kitTypeId: input.kitTypeId, delta: input.delta, reason: input.reason },
      })
    ).data;
  },
  /** Stock handed back: her bag goes down, the warehouse shelf goes up. */
  async returnKits(professionalId: string, input: KitReturnInput) {
    return (
      await request<ProfessionalKits>(`/admin/professionals/${segment(professionalId)}/kits/return`, {
        method: 'POST',
        body: {
          items: input.items.map(({ kitTypeId, quantity }) => ({ kitTypeId, quantity })),
          reason: input.reason,
        },
      })
    ).data;
  },

  /* The Production counter and the warehouse -------------------------------- */

  /** Finds a professional by partner ID, phone number or id, with her bag. */
  async lookupProfessional(q: string) {
    return (await request<CounterLookup>(withQuery('/admin/professionals/lookup', { q: q.trim() }))).data;
  },
  async warehouse() {
    return (await request<WarehouseItem[]>('/admin/kits/warehouse')).data;
  },
  async warehouseMovements(query: WarehouseMovementQuery) {
    return paged<WarehouseMovement>(
      withQuery('/admin/kits/warehouse/movements', {
        kitTypeId: query.kitTypeId,
        professionalId: query.professionalId,
        kind: query.kind,
        page: query.page,
        pageSize: query.pageSize,
      }),
    );
  },
  async receiveStock(kitTypeId: string, input: ReceiveStockInput) {
    return (
      await request<WarehouseItem>(`/admin/kits/warehouse/${segment(kitTypeId)}/receive`, {
        method: 'POST',
        body: { quantity: input.quantity, reference: input.reference, note: input.note },
      })
    ).data;
  },
  async adjustWarehouse(kitTypeId: string, delta: number, reason: string) {
    return (
      await request<WarehouseItem>(`/admin/kits/warehouse/${segment(kitTypeId)}/adjust`, {
        method: 'POST',
        body: { delta, reason },
      })
    ).data;
  },
  async setReorderLevel(kitTypeId: string, reorderLevel: number) {
    return (
      await request<WarehouseItem>(`/admin/kits/warehouse/${segment(kitTypeId)}`, {
        method: 'PUT',
        body: { reorderLevel },
      })
    ).data;
  },

  /* Payments ------------------------------------------------------------------ */

  /** Public: which gateway takes money, if any. */
  async paymentConfig() {
    return (await request<PaymentConfig>('/payments/config')).data;
  },
  async orderPayments(orderId: string) {
    return (await request<OrderPayments>(`/admin/orders/${segment(orderId)}/payments`)).data;
  },
  /** What a dispatched job pays its partner, and how that was worked out. */
  async jobEarnings(jobId: string) {
    return (await request<JobEarnings>(`/admin/jobs/${segment(jobId)}`)).data;
  },
  async refunds(statuses: RefundRecord['status'][]) {
    return all<RefundRecord>(withQuery('/admin/refunds', { status: statuses.join(',') }));
  },
  async retryRefund(id: string) {
    return (await request<RefundRecord>(`/admin/refunds/${segment(id)}/retry`, { method: 'POST' })).data;
  },

  /* Payouts ------------------------------------------------------------------- */

  /** Which payout gateway sends partners' withdrawals, if any. */
  async payoutConfig() {
    return (await request<PayoutGatewayConfig>('/admin/payouts/config')).data;
  },
  /** The queue works oldest first; history reads newest first. */
  async payouts(statuses: PayoutRecord['status'][], page = 1, pageSize = 50, newestFirst = false) {
    return paged<PayoutRecord>(
      withQuery('/admin/payouts', {
        status: statuses.join(','),
        page,
        pageSize,
        sort: newestFirst ? 'newest' : undefined,
      }),
    );
  },
  async settlePayout(id: string, input: PayoutSettleInput) {
    return (
      await request<PayoutRecord>(`/admin/payouts/${segment(id)}/settle`, { method: 'POST', body: input })
    ).data;
  },
  /** Sends a queued payout through the gateway now. */
  async retryPayout(id: string) {
    return (await request<PayoutRecord>(`/admin/payouts/${segment(id)}/retry`, { method: 'POST' })).data;
  },
  /** The full account a payout goes to, for paying it by hand. Each call is audited. */
  async payoutDestination(id: string) {
    return (await request<PayoutDestination>(`/admin/payouts/${segment(id)}/destination`)).data;
  },
  async partnerBalances(filter: 'owing' | 'owed' | 'all', page = 1, pageSize = 50) {
    return paged<PartnerBalance>(withQuery('/admin/payouts/balances', { filter, page, pageSize }));
  },
  /** A manual ledger line: commission a partner paid in, a bonus, a correction. */
  async addLedgerEntry(professionalId: string, input: LedgerEntryInput) {
    await request(`/admin/professionals/${segment(professionalId)}/earnings`, { method: 'POST', body: input });
  },
  /** The latest SMS send attempts, newest first: the quickest check that SMS works. */
  async smsDeliveries(limit = 6) {
    return paged<DeliveryRecord>(
      withQuery('/admin/notifications/deliveries', { channel: 'sms', page: 1, pageSize: limit }),
    );
  },
  async saveCategory(category: ServiceCategory) {
    const value = category as AdminCategory;
    const body = {
      slug: value.slug,
      name: value.name,
      icon: value.icon,
      image: value.image,
      blurb: value.blurb,
      intro: value.intro,
      position: value.position ?? 0,
      active: value.active ?? true,
      metaTitle: value.metaTitle || '',
      metaDescription: value.metaDescription || '',
    };
    const path = value.id ? `/admin/categories/${value.id}` : '/admin/categories';
    return (await request<AdminCategory>(path, { method: value.id ? 'PUT' : 'POST', body })).data;
  },
  async deleteCategory(id: string) {
    await request(`/admin/categories/${id}`, { method: 'DELETE' });
  },
  async saveProfessional(value: Professional) {
    if (!value.id) throw new Error('Professionals are created only through approved applications.');
    return (
      await request<Professional>(`/admin/professionals/${value.id}`, {
        method: 'PATCH',
        body: {
          bio: value.bio,
          specialties: value.specialties,
          categoryIds: value.categoryIds || [],
          city: value.city || '',
          travelRadiusKm: value.travelRadiusKm ?? 10,
          photo: value.photo,
        },
      })
    ).data;
  },
  async setProfessionalAvailable(id: string, available: boolean) {
    return (
      await request<Professional>(`/admin/professionals/${id}/availability`, {
        method: 'POST',
        body: { available },
      })
    ).data;
  },

  async updateCustomer(id: string, patch: Partial<Customer>) {
    return (
      await request<CustomerWire>(`/admin/customers/${id}`, {
        method: 'PATCH',
        body: {
          ...(patch.member !== undefined ? { member: patch.member } : {}),
          ...(patch.blocked !== undefined ? { blocked: patch.blocked } : {}),
          ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
          ...(patch.city !== undefined ? { city: patch.city } : {}),
        },
      })
    ).data;
  },
  /**
   * Field by field, because the server refuses unknown keys and a coupon read back from
   * it carries several (id, usedCount, createdAt, and a credit's customer and booking).
   */
  async saveCoupon(coupon: Coupon) {
    const body = {
      code: coupon.code,
      title: coupon.title,
      description: coupon.description,
      discountPercent: coupon.discountPercent,
      active: coupon.active,
      expiresAt: coupon.expiresAt,
      discountType: coupon.discountType || 'percent',
      flatAmount: coupon.flatAmount || 0,
      maxDiscount: coupon.maxDiscount || 0,
      minSubtotal: coupon.minSubtotal || 0,
      categoryIds: coupon.categoryIds || [],
      serviceIds: coupon.serviceIds || [],
      usageLimit: coupon.usageLimit || 0,
      perUserLimit: coupon.perUserLimit ?? 1,
      membersOnly: Boolean(coupon.membersOnly),
      firstBookingOnly: Boolean(coupon.firstBookingOnly),
      startsAt: coupon.startsAt || null,
    };
    const path = coupon.id ? `/admin/coupons/${coupon.id}` : '/admin/coupons';
    return (await request<Coupon>(path, { method: coupon.id ? 'PUT' : 'POST', body })).data;
  },
  async toggleCoupon(coupon: Coupon) {
    return (
      await request<Coupon>(`/admin/coupons/${coupon.id}/active`, {
        method: 'POST',
        body: { active: !coupon.active },
      })
    ).data;
  },
  async deleteCoupon(id: string) {
    await request(`/admin/coupons/${id}`, { method: 'DELETE' });
  },
  async moderateReview(id: string, state: string, note?: string) {
    return (
      await request<ModeratedReviewDto>(`/admin/reviews/${id}/state`, {
        method: 'POST',
        body: { state, note: note || '' },
      })
    ).data;
  },
  /** `push` also sends it to every customer's phone, besides the in-app feed. */
  async sendNotification(input: Pick<Notification, 'category' | 'title' | 'body'> & { push: boolean }) {
    return (
      await request<Notification>('/admin/notifications/', {
        method: 'POST',
        body: { audience: 'customer', ...input },
      })
    ).data;
  },
  async deleteNotification(id: string) {
    await request(`/admin/notifications/${id}`, { method: 'DELETE' });
  },
  /**
   * Creates a login (no id) or edits one. An edit sends the access only when it
   * changed: an administrator may not change their own, and the server refuses even an
   * unchanged copy from them.
   */
  async saveStaff(user: StaffSaveInput, accessChanged = true) {
    const details = {
      name: user.name,
      email: user.email,
      phone: user.phone,
      jobTitle: user.jobTitle,
      employeeCode: user.employeeCode,
      notes: user.notes,
      city: user.city,
    };
    const access = { role: user.role, capabilities: user.role === 'admin' ? [] : user.capabilities };
    if (user.id) {
      return (
        await request<StaffUser>(`/admin/staff/${segment(user.id)}`, {
          method: 'PATCH',
          body: accessChanged ? { ...details, ...access } : details,
        })
      ).data;
    }
    if (!user.temporaryPassword) throw new Error('Set a temporary password for the new login.');
    return (
      await request<StaffUser>('/admin/staff', {
        method: 'POST',
        body: { ...details, ...access, password: user.temporaryPassword },
      })
    ).data;
  },
  async toggleStaff(user: StaffUser) {
    return (
      await request<StaffUser>(`/admin/staff/${segment(user.id)}/active`, {
        method: 'POST',
        body: { active: !user.active },
      })
    ).data;
  },
  /** A new temporary password, to be changed at the next sign-in. Signs them out everywhere. */
  async resetStaffPassword(id: string, password: string) {
    return (
      await request<StaffUser>(`/admin/staff/${segment(id)}/password`, {
        method: 'POST',
        body: { password },
        retry: false,
      })
    ).data;
  },

  /* Banners and the website hero --------------------------------------------- */

  async banners() {
    return (await request<AdminBanner[]>('/admin/banners')).data;
  },
  async saveBanner(input: BannerSaveInput) {
    const { id, ...body } = input;
    return (
      await request<AdminBanner>(id ? `/admin/banners/${segment(id)}` : '/admin/banners', {
        method: id ? 'PUT' : 'POST',
        body,
      })
    ).data;
  },
  async deleteBanner(id: string) {
    await request(`/admin/banners/${segment(id)}`, { method: 'DELETE' });
  },
  /** The full carousel order, every slide once. */
  async orderBanners(ids: string[]) {
    return (await request<AdminBanner[]>('/admin/banners/order', { method: 'POST', body: { ids } })).data;
  },
  async hero() {
    return (await request<AdminHero>('/admin/hero')).data;
  },
  async saveHero(input: HeroSaveInput) {
    return (await request<AdminHero>('/admin/hero', { method: 'PUT', body: input })).data;
  },
  /** Stores a photo and returns the path to keep in an image field. */
  async uploadImage(file: File) {
    return uploadFile<MediaUpload>('/admin/media', file);
  },
  async updateSettings(patch: Partial<SettingsDto>) {
    return (await request<SettingsDto>('/admin/settings', { method: 'PATCH', body: patch })).data;
  },
  /** The settings as saved now. Their city list follows the cities open in Locations. */
  async settings() {
    return (await request<SettingsDto>('/admin/settings')).data;
  },
};
